import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import {
  Users, UserCheck, Briefcase, Building2, IndianRupee, Send,
  TrendingUp, Activity, ShieldAlert, MessageSquareWarning,
  BarChart3, RefreshCw, Target, Sparkles, Clock, CreditCard,
  Star, Percent, Layers, Award
} from 'lucide-react';
import { BIDashboardResponse, CountItem } from '../Types/BIDashboardType';

interface BIDashboardComponentProps {
  data: BIDashboardResponse | null;
  loading: boolean;
  onRefresh: () => void;
}

/* ─── Reusable building blocks ─────────────────────────────────────── */

const StatCard = ({
  label, value, icon, gradient, sub,
}: {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  gradient: string;
  sub?: string;
}) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.4 }}
    className={`relative overflow-hidden rounded-2xl p-5 text-white shadow-lg ${gradient}`}
  >
    <div className="absolute -right-4 -top-4 h-24 w-24 rounded-full bg-white/10 blur-2xl" />
    <div className="relative z-10 flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <span className="rounded-lg bg-white/20 p-2 backdrop-blur-md">{icon}</span>
        <span className="text-[10px] font-bold uppercase tracking-wider text-white/80">Live</span>
      </div>
      <div>
        <h4 className="text-3xl font-black">{value}</h4>
        <p className="text-sm font-bold opacity-90">{label}</p>
      </div>
      {sub && <p className="text-[10px] font-medium text-white/70 italic">{sub}</p>}
    </div>
  </motion.div>
);

const SectionCard = ({
  title, icon, children, right,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
  right?: React.ReactNode;
}) => (
  <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm">
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        <span className="w-9 h-9 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
          {icon}
        </span>
        <h3 className="text-base font-bold text-slate-800">{title}</h3>
      </div>
      {right}
    </div>
    {children}
  </div>
);

const BarList = ({ items, color = 'bg-indigo-500' }: { items: CountItem[]; color?: string }) => {
  const max = useMemo(() => Math.max(1, ...(items || []).map((i) => i.value)), [items]);
  if (!items || items.length === 0) {
    return <p className="text-sm text-slate-400">No data yet.</p>;
  }
  return (
    <div className="flex flex-col gap-2.5">
      {items.map((item, idx) => (
        <div key={`${item.label}-${idx}`} className="flex items-center gap-3">
          <span className="w-32 truncate text-xs font-semibold text-slate-600 text-right">{item.label}</span>
          <div className="flex-1 h-2.5 rounded-full bg-slate-100 overflow-hidden">
            <div
              className={`h-full ${color} rounded-full transition-all duration-700`}
              style={{ width: `${Math.max(2, (item.value / max) * 100)}%` }}
            />
          </div>
          <span className="w-10 text-xs font-bold text-slate-700">{item.value}</span>
        </div>
      ))}
    </div>
  );
};

const MiniStat = ({ label, value, icon }: { label: string; value: string | number; icon: React.ReactNode }) => (
  <div className="flex items-center justify-between px-4 py-3 rounded-xl bg-slate-50">
    <span className="flex items-center gap-2 text-xs font-semibold text-slate-500">
      {icon}
      {label}
    </span>
    <span className="text-sm font-black text-slate-800">{value}</span>
  </div>
);

const formatDuration = (seconds: number | null | undefined): string => {
  if (seconds === null || seconds === undefined || Number.isNaN(seconds)) return '—';
  if (seconds < 60) return `${Math.round(seconds)}s`;
  if (seconds < 3600) return `${Math.round(seconds / 60)}m`;
  return `${Math.round(seconds / 3600)}h`;
};

/* ─── Main Dashboard ────────────────────────────────────────────────── */

function BIDashboardComponent({ data, loading, onRefresh }: BIDashboardComponentProps) {
  const [lastUpdated] = useState<string>(new Date().toLocaleString());

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-6 md:p-12 flex items-center justify-center">
        <div className="flex flex-col items-center gap-4 text-slate-400">
          <RefreshCw className="w-10 h-10 animate-spin text-indigo-500" />
          <p className="text-sm font-semibold">Loading platform intelligence…</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] p-6 md:p-12 flex items-center justify-center">
        <div className="text-center">
          <p className="text-lg font-bold text-slate-600 mb-2">Unable to load dashboard</p>
          <button
            onClick={onRefresh}
            className="px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-bold hover:bg-indigo-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const po = data.platformOverview;
  const ci = data.candidateIntelligence;
  const ra = data.recruitmentActivity;
  const rp = data.revenuePayments;
  const ea = data.earlyAccessGrowth;
  const ph = data.platformHealth;
  const fb = data.feedbackSupport;

  return (
    <div className="min-h-screen bg-[#F8FAFC] p-6 md:p-12">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <header className="mb-10">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 tracking-tight flex items-center gap-3">
                <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-600 to-fuchsia-600 text-white flex items-center justify-center">
                  <BarChart3 size={24} />
                </span>
                Business Intelligence
              </h1>
              <p className="text-slate-500 mt-2 font-medium">
                Real-time platform analytics — Nexgent admin panel.
              </p>
            </div>
            <button
              onClick={onRefresh}
              className="px-5 py-2.5 rounded-xl bg-white border border-slate-200 text-slate-700 text-sm font-bold hover:bg-slate-50 transition-colors flex items-center gap-2 self-start md:self-center shadow-sm"
            >
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>
          <p className="mt-3 text-xs text-slate-400 font-medium">Last updated: {lastUpdated}</p>
        </header>

        {/* ── KPI Row ── */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <StatCard
            label="Total Users"
            value={po.totalUsers.toLocaleString()}
            icon={<Users className="w-5 h-5" />}
            gradient="bg-gradient-to-br from-blue-600 to-indigo-700"
            sub={`${po.usersRegisteredThisMonth.toLocaleString()} new this month`}
          />
          <StatCard
            label="Candidates"
            value={ci.totalCandidates.toLocaleString()}
            icon={<Target className="w-5 h-5" />}
            gradient="bg-gradient-to-br from-emerald-500 to-teal-700"
            sub={`${ci.activelyExploring.toLocaleString()} actively exploring`}
          />
          <StatCard
            label="Total Revenue"
            value={`₹${rp.totalRevenue.toLocaleString(undefined, { maximumFractionDigits: 0 })}`}
            icon={<IndianRupee className="w-5 h-5" />}
            gradient="bg-gradient-to-br from-amber-500 to-orange-600"
            sub={`₹${rp.revenueThisMonth.toLocaleString(undefined, { maximumFractionDigits: 0 })} this month`}
          />
          <StatCard
            label="Invitations Sent"
            value={ra.totalInvitations.toLocaleString()}
            icon={<Send className="w-5 h-5" />}
            gradient="bg-gradient-to-br from-fuchsia-500 to-purple-700"
            sub={`${ra.acceptanceRate}% acceptance rate`}
          />
        </div>

        {/* ── Platform Overview ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <SectionCard
            title="User Status"
            icon={<Users size={18} />}
            right={<span className="text-xs font-bold text-slate-400">Platform Overview</span>}
          >
            <div className="flex flex-col gap-2.5">
              <MiniStat label="Active" value={po.activeUsers} icon={<span className="w-2 h-2 rounded-full bg-emerald-500" />} />
              <MiniStat label="Under Review" value={po.underReviewUsers} icon={<span className="w-2 h-2 rounded-full bg-amber-500" />} />
              <MiniStat label="Rejected" value={po.rejectedUsers} icon={<span className="w-2 h-2 rounded-full bg-rose-500" />} />
              <MiniStat label="Pending" value={po.pendingUsers} icon={<span className="w-2 h-2 rounded-full bg-slate-400" />} />
            </div>
          </SectionCard>

          <SectionCard
            title="Registrations"
            icon={<TrendingUp size={18} />}
            right={<Sparkles size={16} className="text-yellow-500" />}
          >
            <div className="flex flex-col gap-2.5">
              <MiniStat label="Today" value={po.usersRegisteredToday} icon={<Clock size={14} />} />
              <MiniStat label="This Week" value={po.usersRegisteredThisWeek} icon={<Clock size={14} />} />
              <MiniStat label="This Month" value={po.usersRegisteredThisMonth} icon={<Clock size={14} />} />
              <MiniStat label="Founding Members" value={po.foundingMembers} icon={<Award size={14} />} />
            </div>
          </SectionCard>

          <SectionCard
            title="Workforce Split"
            icon={<Layers size={18} />}
          >
            <BarList items={po.usersByRole} color="bg-indigo-500" />
          </SectionCard>
        </div>

        {/* ── Candidate Intelligence ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <SectionCard
            title="Average Scores"
            icon={<Star size={18} />}
          >
            <div className="flex flex-col gap-2.5">
              <MiniStat label="Leadership" value={ci.avgLeadershipScore} icon={<Star size={14} />} />
              <MiniStat label="Architecture" value={ci.avgArchitectureScore} icon={<Layers size={14} />} />
              <MiniStat label="Business Impact" value={ci.avgBusinessImpactScore} icon={<TrendingUp size={14} />} />
              <MiniStat label="Overall" value={ci.avgOverallScore} icon={<Percent size={14} />} />
            </div>
          </SectionCard>

          <SectionCard
            title="Candidate Growth"
            icon={<TrendingUp size={18} />}
          >
            <div className="flex flex-col gap-2.5">
              <MiniStat label="New (7 days)" value={ci.newCandidates7Days} icon={<Clock size={14} />} />
              <MiniStat label="New (30 days)" value={ci.newCandidates30Days} icon={<Clock size={14} />} />
              <MiniStat label="Updated Today" value={ci.updatedToday} icon={<RefreshCw size={14} />} />
              <MiniStat label="Profile Complete" value={`${ci.profileCompleted} / ${ci.totalCandidates}`} icon={<Target size={14} />} />
            </div>
          </SectionCard>

          <SectionCard
            title="Availability"
            icon={<ShieldAlert size={18} />}
          >
            <BarList items={ci.byAvailabilityStatus} color="bg-emerald-500" />
          </SectionCard>
        </div>

        {/* ── Role Level / Domain / City ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          <SectionCard title="By Role Level" icon={<Briefcase size={18} />}>
            <BarList items={ci.byRoleLevel} color="bg-blue-500" />
          </SectionCard>
          <SectionCard title="By Domain" icon={<Building2 size={18} />}>
            <BarList items={ci.byDomainExpertise} color="bg-purple-500" />
          </SectionCard>
          <SectionCard title="Top Cities" icon={<Building2 size={18} />}>
            <BarList items={ci.byCity} color="bg-cyan-500" />
          </SectionCard>
        </div>

        {/* ── Recruitment & Revenue ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <SectionCard
            title="Recruitment Activity"
            icon={<UserCheck size={18} />}
            right={<span className="text-xs font-bold text-slate-400">Invitations</span>}
          >
            <div className="flex flex-col gap-2.5 mb-4">
              <MiniStat label="Pending" value={ra.pendingInvitations} icon={<Clock size={14} />} />
              <MiniStat label="Accepted" value={ra.acceptedInvitations} icon={<UserCheck size={14} />} />
              <MiniStat label="Rejected" value={ra.rejectedInvitations} icon={<Users size={14} />} />
              <MiniStat label="Avg Response Time" value={formatDuration(ra.avgResponseTimeSeconds)} icon={<Clock size={14} />} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="This Week" value={ra.invitationsThisWeek} icon={<Clock size={14} />} />
              <MiniStat label="This Month" value={ra.invitationsThisMonth} icon={<Clock size={14} />} />
              <MiniStat label="Active Jobs" value={ra.activeJobTemplates} icon={<Briefcase size={14} />} />
              <MiniStat label="Total Jobs" value={ra.totalJobTemplates} icon={<Layers size={14} />} />
            </div>
          </SectionCard>

          <SectionCard
            title="Revenue & Payments"
            icon={<CreditCard size={18} />}
            right={<span className="text-xs font-bold text-slate-400">Transactions</span>}
          >
            <div className="flex flex-col gap-2.5 mb-4">
              <MiniStat label="Successful Payments" value={rp.successfulPayments} icon={<span className="w-2 h-2 rounded-full bg-emerald-500" />} />
              <MiniStat label="Failed Payments" value={rp.failedPayments} icon={<span className="w-2 h-2 rounded-full bg-rose-500" />} />
              <MiniStat label="Credits Purchased" value={rp.totalCreditsPurchased.toLocaleString()} icon={<CreditCard size={14} />} />
              <MiniStat label="Credit Transactions" value={rp.totalCreditTransactions} icon={<Layers size={14} />} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <MiniStat label="Revenue this week" value={`₹${rp.revenueThisWeek.toLocaleString()}`} icon={<IndianRupee size={14} />} />
              <MiniStat label="Revenue this month" value={`₹${rp.revenueThisMonth.toLocaleString()}`} icon={<IndianRupee size={14} />} />
            </div>
          </SectionCard>
        </div>

        {/* ── Payments by plan + Early Access ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <SectionCard title="Sales by Plan" icon={<CreditCard size={18} />}>
            <BarList items={rp.paymentsByPlan} color="bg-amber-500" />
          </SectionCard>
          <SectionCard title="Early Access & Growth" icon={<Sparkles size={18} />}>
            <div className="flex flex-col gap-2.5 mb-4">
              <MiniStat label="This Week" value={ea.signupsThisWeek} icon={<Clock size={14} />} />
              <MiniStat label="This Month" value={ea.signupsThisMonth} icon={<Clock size={14} />} />
              <MiniStat label="Founding Members" value={ea.foundingMembers} icon={<Award size={14} />} />
            </div>
            <BarList items={ea.byPlatformRole} color="bg-fuchsia-500" />
          </SectionCard>
        </div>

        {/* ── Platform Health & Feedback ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <SectionCard
            title="Platform Health"
            icon={<Activity size={18} />}
            right={<span className="text-xs font-bold text-slate-400">24h window</span>}
          >
            <div className="flex flex-col gap-2.5 mb-4">
              <MiniStat label="Audit Events (24h)" value={ph.totalAuditEvents24h} icon={<Activity size={14} />} />
              <MiniStat label="Failed Events (24h)" value={ph.failedAuditEvents24h} icon={<ShieldAlert size={14} />} />
              <MiniStat label="App Events (24h)" value={ph.totalApplicationEvents24h} icon={<Activity size={14} />} />
              <MiniStat label="Total Audit Events" value={ph.totalAuditEventsAllTime} icon={<Layers size={14} />} />
            </div>
            <BarList items={ph.auditEventsByAction} color="bg-rose-500" />
          </SectionCard>

          <SectionCard
            title="Feedback & Support"
            icon={<MessageSquareWarning size={18} />}
          >
            <div className="flex flex-col gap-2.5 mb-4">
              <MiniStat label="Open" value={fb.openFeedback} icon={<span className="w-2 h-2 rounded-full bg-amber-500" />} />
              <MiniStat label="In Progress" value={fb.inProgressFeedback} icon={<span className="w-2 h-2 rounded-full bg-blue-500" />} />
              <MiniStat label="Resolved" value={fb.resolvedFeedback} icon={<span className="w-2 h-2 rounded-full bg-emerald-500" />} />
              <MiniStat label="This Month" value={fb.feedbackThisMonth} icon={<Clock size={14} />} />
            </div>
            <BarList items={fb.byCategory} color="bg-indigo-500" />
          </SectionCard>
        </div>

      </div>
    </div>
  );
}

export default BIDashboardComponent;